/**
 * ToggleSwitch - Accessible toggle switch component (WP-CAT-11)
 *
 * Extracted from NotificationSettings.tsx for reusability.
 * Follows WCAG 2.1 AA accessibility guidelines.
 *
 * Features:
 * - Keyboard accessible (Space/Enter to toggle)
 * - Screen reader support with role="switch" and aria-checked
 * - Loading state with spinner
 * - Focus ring styling
 * - Mobile touch target (min 44px width)
 */
import { cn } from '@/utils/cn';

export interface ToggleSwitchProps {
  /** Current enabled state */
  enabled: boolean;
  /** Callback when toggle is clicked */
  onChange: (enabled: boolean) => void;
  /** Disable the toggle */
  disabled?: boolean;
  /** Show loading spinner on the knob */
  isLoading?: boolean;
  /** Unique ID for accessibility linking */
  id: string;
  /** Accessible label for screen readers */
  label: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Toggle switch component following accessibility best practices.
 *
 * Pattern source: NotificationSettings.tsx ToggleSwitch component
 *
 * Accessibility:
 * - Uses role="switch" for proper screen reader announcement
 * - aria-checked indicates current state
 * - aria-labelledby links to visible label element
 * - Focus visible ring for keyboard navigation
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   id="category-enabled"
 *   label="Enable category"
 *   enabled={isEnabled}
 *   onChange={(enabled) => updatePreference(enabled)}
 * />
 * ```
 */
export default function ToggleSwitch({
  enabled,
  onChange,
  disabled = false,
  isLoading = false,
  id,
  label,
  size = 'md',
}: ToggleSwitchProps) {
  // Size variants - md is the default from NotificationSettings
  const sizeStyles = {
    sm: {
      track: 'h-5 w-9',
      knob: 'h-4 w-4',
      translate: enabled ? 'translate-x-4' : 'translate-x-0',
      spinner: 'h-3 w-3',
    },
    md: {
      track: 'h-6 w-11',
      knob: 'h-5 w-5',
      translate: enabled ? 'translate-x-5' : 'translate-x-0',
      spinner: 'h-3 w-3',
    },
    lg: {
      track: 'h-7 w-14',
      knob: 'h-6 w-6',
      translate: enabled ? 'translate-x-7' : 'translate-x-0',
      spinner: 'h-4 w-4',
    },
  };

  const styles = sizeStyles[size];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-labelledby={`${id}-label`}
      id={id}
      disabled={disabled || isLoading}
      onClick={() => onChange(!enabled)}
      className={cn(
        'relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        enabled ? 'bg-primary-600' : 'bg-gray-200',
        styles.track
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-flex items-center justify-center transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          styles.knob,
          styles.translate
        )}
      >
        {isLoading && (
          <svg
            className={cn('animate-spin text-gray-400', styles.spinner)}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </span>
    </button>
  );
}
